import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type {
  ComponentLiveState,
  DestinationLiveStateSnapshot,
  JsonValue,
} from '../types'

const LiveContentContext = createContext<DestinationLiveStateSnapshot | null>(null)

export function LiveContentProvider({
  snapshot,
  children,
}: {
  snapshot: DestinationLiveStateSnapshot
  children: ReactNode
}) {
  const [now, setNow] = useState(() => Date.now())
  const nextExpiry = useMemo(
    () =>
      Object.values(snapshot.components)
        .map((component) => component.expiresAt && Date.parse(component.expiresAt))
        .filter((value): value is number =>
          typeof value === 'number' && Number.isFinite(value) && value > now)
        .sort((left, right) => left - right)[0] ?? null,
    [now, snapshot.components],
  )
  useEffect(() => {
    if (nextExpiry == null) return
    const timeout = window.setTimeout(
      () => setNow(Date.now()),
      Math.max(0, nextExpiry - Date.now() + 10),
    )
    return () => window.clearTimeout(timeout)
  }, [nextExpiry])

  const activeSnapshot = useMemo<DestinationLiveStateSnapshot>(
    () => ({
      ...snapshot,
      components: Object.fromEntries(
        Object.entries(snapshot.components).filter(([, component]) => {
          if (!component.expiresAt) return true
          const expiry = Date.parse(component.expiresAt)
          return !Number.isFinite(expiry) || expiry > now
        }),
      ),
    }),
    [now, snapshot],
  )

  return (
    <LiveContentContext.Provider value={activeSnapshot}>
      {children}
    </LiveContentContext.Provider>
  )
}

export function useLiveContentRevision(): number {
  const context = useContext(LiveContentContext)
  if (!context) throw new Error('Live content hooks must be used inside <LiveContentProvider>.')
  return context.revision
}

export function useComponentLiveState<T extends JsonValue = JsonValue>(
  instanceId: string,
): ComponentLiveState<T> {
  const context = useContext(LiveContentContext)
  if (!context) throw new Error('Live content hooks must be used inside <LiveContentProvider>.')
  return (context.components[instanceId] as ComponentLiveState<T> | undefined) ?? {
    state: null,
    revision: context.revision,
    updatedAt: null,
    expiresAt: null,
  }
}
